"use client";

import { SafeHydrate } from "@/SafeHydrate";
import { useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const testimonials = [{
  quote: "Pixel Force transformed my competitive journey. The community support and professional resources are unmatched in the esports scene.",
  author: "Alex R.",
  position: "Pro Player",
  portrait: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701752/img/pro_player_alex.png",
  portraitSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701752/img/pro_player_alex.png-wvc-srcset",
  portraitSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701752/img/pro_player_alex.png-wvc-sizes",
  avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701752/img/avatar_alex.png"
}, {
  quote: "The tactical insights and tournament schedules provided by the team have significantly leveled up my gameplay. Essential for any serious gamer.",
  author: "Sarah K.",
  position: "Community Member",
  portrait: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701752/img/community_member_sarah.png",
  portraitSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701752/img/community_member_sarah.png-wvc-srcset",
  portraitSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701752/img/community_member_sarah.png-wvc-sizes",
  avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701752/img/avatar_sarah.webp"
}, {
  quote: "Pixel Force isn't just a team; it's a command center. The atmosphere is electric, and the dedication to excellence is visible in every match.",
  author: "Marcus V.",
  position: "Tournament Champion",
  portrait: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701752/img/champion_marcus.png",
  portraitSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701752/img/champion_marcus.png-wvc-srcset",
  portraitSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701752/img/champion_marcus.png-wvc-sizes",
  avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701752/img/avatar_marcus.webp"
}];
export default function FanFeedback() {
  const [activeIndex, setActiveIndex] = useState(0);
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const handleSelect = (i: number) => setActiveIndex(i);
  const handlePrev = () => setActiveIndex(prev => (prev - 1 + testimonials.length) % testimonials.length);
  const handleNext = () => setActiveIndex(prev => (prev + 1) % testimonials.length);
  const active = testimonials[activeIndex];
  return <section data-section-id="3598" id="fan-feedback" className="bg-background text-foreground py-24 md:py-36 overflow-hidden">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <div ref={ref} className={`transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          {}
          <div className="mb-16 md:mb-24">
            <span className="font-default text-xs tracking-[0.3em] uppercase text-muted-foreground">— COMMUNITY INTEL</span>
          </div>

          {}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            {}
            <div className="lg:col-span-5 order-1">
              <div key={`portrait-${activeIndex}`} className="animate-in fade-in duration-700">
                <div className="overflow-hidden rounded-lg shadow-lg">
                  <AspectRatio ratio={3 / 4}>
                    <img src={active.portrait} data-wvc-srcset={active.portraitSrcset} data-wvc-sizes={active.portraitSizes} alt={`Portrait of ${active.author}`} loading="lazy" decoding="async" className="h-full w-full object-cover" />
                  </AspectRatio>
                </div>
                <p className="mt-6 font-default italic text-base text-muted-foreground">
                  {active.author} — {active.position}
                </p>
              </div>
            </div>

            {}
            <div className="lg:col-span-7 order-2 lg:pl-6">
              <div key={`quote-${activeIndex}`} className="animate-in fade-in duration-700">
                {}
                <div aria-hidden="true" className="font-default leading-none text-[8rem] md:text-[10rem]" style={{
                color: "hsl(180 100% 50%)"
              }}>
                  &ldquo;
                </div>

                <blockquote className="-mt-8 md:-mt-12">
                  <p className="font-default italic text-2xl md:text-[2.25rem] leading-[1.3] text-foreground">
                    {active.quote}
                  </p>
                </blockquote>
              </div>

              {}
              <Separator className="my-10 bg-border" />

              {}
              <div className="flex items-center justify-between gap-6 flex-wrap">
                <div className="flex items-center gap-4 overflow-x-auto">
                  {testimonials.map((t, i) => <Button key={i} data-index={i} onClick={() => handleSelect(i)} variant="ghost" aria-label={`View testimonial from ${t.author}`} className={`group flex items-center gap-3 px-0 transition-opacity duration-500 ${i === activeIndex ? "opacity-100" : "opacity-50 hover:opacity-80"}`}>
                      <SafeHydrate><Avatar className={`size-12 rounded-lg transition-all duration-500 ${i === activeIndex ? "ring-2 ring-offset-2 ring-offset-background" : ""}`} style={i === activeIndex ? {
                      boxShadow: "0 0 0 2px hsl(180 100% 50%)"
                    } : undefined}>
                        <AvatarImage src={t.avatar} alt={t.author} className="object-cover" />
                        <AvatarFallback className="font-default text-sm bg-secondary text-secondary-foreground">
                          {t.author.charAt(0)}
                        </AvatarFallback>
                      </Avatar></SafeHydrate>
                      <span className="hidden md:inline font-default italic text-sm text-muted-foreground whitespace-nowrap">
                        {t.author.split(" ")[0]}
                      </span>
                    </Button>)}
                </div>

                {}
                <div className="flex items-center gap-6">
                  {}
                  <div className="flex items-center gap-2">
                    {testimonials.map((_, i) => <Button key={i} data-index={i} onClick={() => handleSelect(i)} variant="ghost" size="icon" aria-label={`Go to testimonial ${i + 1}`} className="size-2 rounded-lg p-0 transition-all duration-300" style={{
                    backgroundColor: i === activeIndex ? "hsl(180 100% 50%)" : "hsl(240 5% 65%)",
                    transform: i === activeIndex ? "scale(1.3)" : "scale(1)"
                  }} />)}
                  </div>

                  {}
                  <div className="flex items-center gap-2">
                    <Button variant="ghost" size="icon" onClick={handlePrev} aria-label="Previous testimonial" className="rounded-lg text-foreground hover:bg-accent hover:text-accent-foreground">
                      <ChevronLeft className="size-5" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={handleNext} aria-label="Next testimonial" className="rounded-lg text-foreground hover:bg-accent hover:text-accent-foreground">
                      <ChevronRight className="size-5" />
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}